import { useContext } from 'react';
import { UserContext } from '@services/Session';
import { analytics, Dict, UserType } from '@services/analytics';
import segment from '@services/segment';
import {
  PaywallPageContext,
  PaywallEvents,
} from '@components/Paywall/paywall.types';
import { getUserTypeRelativeToTeam } from '@helpers/utils/teamUtils';
import { usePlayerPrimaryInfo } from '@features/Player/playerHooks';
import axios from '@helpers/axios';

export const usePaywallTracking = (
  context: PaywallPageContext,
  playerId: string = ''
) => {
  const userStore = useContext(UserContext);
  const { playerInfo } = usePlayerPrimaryInfo(playerId);

  const userTeamIds = userStore.userTeams
    ? userStore.userTeams.map((team) => team._id)
    : [];
  const userPlayerIds = userStore.userPlayers
    ? userStore.userPlayers.map((p) => p._id)
    : [];
  const isAdmin = userPlayerIds.includes(playerId);
  const isFollower =
    playerInfo?.teams &&
    playerInfo.teams.length &&
    userStore.teamFollowerIds
      .concat(userTeamIds)
      .filter((teamId) =>
        playerInfo.teams.map((playerTeam) => playerTeam._id).includes(teamId)
      ).length;

  const userType = () => {
    let user: UserType;
    if (!userStore.authUser) {
      user = UserType.LoggedOut;
    } else if (isAdmin) {
      user = UserType.Admin;
    } else if (isFollower) {
      user = UserType.CommunityMember;
    } else {
      user = UserType.LoggedIn;
    }
    return user;
  };

  const trackPaywallEvent = (eventName: PaywallEvents, payload: Dict = {}) => {
    analytics.track(eventName, {
      context,
      userType: userType(),
      ...payload,
    });

    segment.track(eventName, {
      context,
      userType: userType(),
      ...payload,
    });
  };

  const trackPaywallGameViewEvent = async (
    gameId: string,
    teamPlayerId: string
  ) => {
    const teamPlayerInfo = await axios
      .get(`/api/team_player/${teamPlayerId}`)
      .then((response) => {
        return response.data;
      });

    analytics.track(PaywallEvents.GAME_PAGE_VIEW_BUTTON_CLICKED, {
      context: PaywallPageContext.VIEW_BUTTON,
      gameId,
      playerId: teamPlayerInfo.teamPlayer._id,
      teamId: teamPlayerInfo.team._id,
      sportType: teamPlayerInfo.team.attrib_sportType,
      userType: getUserTypeRelativeToTeam(teamPlayerInfo.team._id, userStore),
    });
  };

  return { trackPaywallEvent, trackPaywallGameViewEvent };
};
